class CartDrawerChroxel extends HTMLElement {
  constructor() {
    super();

    this.overlay = this.querySelector('.cart-drawer-chroxel__overlay');
    this.inner = this.querySelector('.cart-drawer-chroxel__panel');
    this.mobileBreakpoint = 749;
    this._dragHandlersAttached = false;
    this._pointerStartY = 0;
    this._isDragging = false;

    if (this.overlay) {
      this.overlay.addEventListener('click', () => this.close());
    }
    this.addEventListener('click', (event) => {
      if (event.target === this) this.close();
    });
    this.addEventListener('keyup', (event) => event.code === 'Escape' && this.close());

    this.setHeaderCartIconAccessibility();
  }

  setHeaderCartIconAccessibility() {
    const cartLink = document.querySelector('#cart-icon-bubble');
    if (!cartLink) return;

    cartLink.setAttribute('role', 'button');
    cartLink.setAttribute('aria-haspopup', 'dialog');
    cartLink.addEventListener('click', (event) => {
      event.preventDefault();
      this.open(cartLink);
    });
    cartLink.addEventListener('keydown', (event) => {
      if (event.code.toUpperCase() === 'SPACE') {
        event.preventDefault();
        this.open(cartLink);
      }
    });
  }

  open(triggeredBy) {
    if (this.classList.contains('active')) return;
    if (triggeredBy) this.activeElement = triggeredBy;

    setTimeout(() => {
      this.classList.add('animate', 'active');
    });

    this.addEventListener(
      'transitionend',
      () => {
        const focusTarget =
          this.querySelector('.cart-drawer-chroxel__close') ||
          this.querySelector('.cart-drawer-chroxel__pill') ||
          this.querySelector('.cart-drawer-chroxel__panel');
        if (typeof trapFocus === 'function') {
          trapFocus(this, focusTarget);
        }
      },
      { once: true },
    );

    document.body.classList.add('overflow-hidden');

    const itemsEl = this.querySelector('cart-drawer-items');
    if (itemsEl && typeof itemsEl.dispatchViewEvent === 'function') {
      itemsEl.dispatchViewEvent();
    }
  }

  close() {
    if (!this.classList.contains('active')) return;
    this.classList.remove('active');
    if (typeof removeTrapFocus === 'function') {
      removeTrapFocus(this.activeElement);
    }
    document.body.classList.remove('overflow-hidden');
  }

  connectedCallback() {
    this.setupBottomSheet();
  }

  disconnectedCallback() {
    this.detachDragHandlers();
  }

  setupBottomSheet() {
    if (window.matchMedia(`(max-width: ${this.mobileBreakpoint}px)`).matches) {
      this.attachDragHandlers();
    }

    this._mediaQuery = window.matchMedia(`(max-width: ${this.mobileBreakpoint}px)`);
    this._mediaQueryHandler = (event) => {
      if (event.matches) {
        this.attachDragHandlers();
      } else {
        this.detachDragHandlers();
      }
    };
    this._mediaQuery.addEventListener('change', this._mediaQueryHandler);
  }

  attachDragHandlers() {
    if (this._dragHandlersAttached || !this.inner) return;
    this._dragHandlersAttached = true;
    this.inner.addEventListener('pointerdown', this.onPointerDown);
  }

  detachDragHandlers() {
    if (!this._dragHandlersAttached) return;
    this._dragHandlersAttached = false;
    if (this.inner) {
      this.inner.removeEventListener('pointerdown', this.onPointerDown);
    }
    this.detachMoveHandlers();
  }

  detachMoveHandlers() {
    if (!this.inner) return;
    this.inner.removeEventListener('pointermove', this.onPointerMove);
    this.inner.removeEventListener('pointerup', this.onPointerEnd);
    this.inner.removeEventListener('pointercancel', this.onPointerEnd);
  }

  onPointerDown = (event) => {
    if (event.button !== 0) return;
    if (event.target.closest('button, a, input, select, textarea, [contenteditable], [role="button"]')) return;
    if (!this.classList.contains('active')) return;
    if (!this.inner) return;

    const rect = this.inner.getBoundingClientRect();
    const offsetFromTop = event.clientY - rect.top;
    if (offsetFromTop > 60) return;

    this._pointerStartY = event.clientY;
    this._isDragging = true;
    this.inner.setPointerCapture(event.pointerId);

    this.inner.addEventListener('pointermove', this.onPointerMove);
    this.inner.addEventListener('pointerup', this.onPointerEnd);
    this.inner.addEventListener('pointercancel', this.onPointerEnd);
  };

  onPointerMove = (event) => {
    if (!this._isDragging || !this.inner) return;
    const delta = event.clientY - this._pointerStartY;
    if (delta < 0) return;

    this.inner.style.transition = 'none';
    this.inner.style.transform = `translateY(${delta}px)`;
  };

  onPointerEnd = (event) => {
    if (!this._isDragging || !this.inner) return;
    this._isDragging = false;
    this.detachMoveHandlers();

    const delta = event.clientY - this._pointerStartY;
    const rect = this.inner.getBoundingClientRect();
    const threshold = Math.max(rect.height * 0.2, 100);

    this.inner.style.transition = '';
    this.inner.style.transform = '';

    if (delta > threshold) {
      this.close();
    }

    this._pointerStartY = 0;
  };

  getSectionsToRender() {
    return [
      {
        id: 'cart-drawer',
        section: 'cart-drawer',
        selector: '.cart-drawer-chroxel__panel',
      },
      {
        id: 'cart-icon-bubble',
        section: 'cart-icon-bubble',
        // DOM target is the header cart link; the section HTML is extracted from its .shopify-section wrapper.
        selector: '#cart-icon-bubble',
        sourceSelector: '.shopify-section',
      },
    ];
  }

  handleCartUpdate(event) {
    const itemCount = event?.cartData?.item_count;
    if (typeof itemCount !== 'number') return;
    this.classList.toggle('is-empty', itemCount === 0);
  }

  setActiveElement(element) {
    this.activeElement = element;
  }

  getSectionInnerHTML(html, selector = '.shopify-section') {
    return new DOMParser().parseFromString(html, 'text/html').querySelector(selector).innerHTML;
  }

  renderContents(parsedState) {
    this.productId = parsedState.id;

    this.getSectionsToRender().forEach((section) => {
      const sectionElement = section.selector
        ? document.querySelector(section.selector)
        : document.getElementById(section.id);

      const sectionHTML = parsedState.sections[section.id];
      if (!sectionElement || !sectionHTML) return;
      sectionElement.innerHTML = this.getSectionInnerHTML(sectionHTML, section.sourceSelector || section.selector);
    });

    this.classList.remove('is-empty');

    // The panel contents were replaced, so re-bind the overlay close handler.
    this.overlay = this.querySelector('.cart-drawer-chroxel__overlay');
    if (this.overlay) {
      this.overlay.addEventListener('click', () => this.close());
    }

    setTimeout(() => {
      this.open(this.activeElement);
    });
  }
}

customElements.define('cart-drawer-chroxel', CartDrawerChroxel);

if (typeof subscribe === 'function' && PUB_SUB_EVENTS?.cartUpdate) {
  document.addEventListener('DOMContentLoaded', () => {
    document.querySelectorAll('cart-drawer-chroxel').forEach((drawer) => {
      drawer.cartUpdateUnsubscriber = subscribe(PUB_SUB_EVENTS.cartUpdate, (event) => {
        if (event.source === 'cart-drawer-chroxel') return;
        drawer.handleCartUpdate(event);
      });
    });
  });
}
